import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BadgeCheck,
  Building2,
  CircleAlert,
  CircleCheck,
  CircleHelp,
  ExternalLink,
  FileCheck2,
  Landmark,
  Percent,
  Search,
  ShieldQuestion,
} from "lucide-react";
import {
  DashboardShell,
  LoadingPanel,
  PageHeading,
  useAggregates,
} from "@/components/DashboardShell";
import {
  access2MarketsUrl,
  assessRexReadiness,
  compareCompetitorRegimes,
  compareMemberStates,
  computeEuLandedCost,
  describeDuty,
  EU_MEMBER_STATE_LIST,
  loadTariffStore,
  REGIME_LABELS,
  searchLines,
  taricConsultationUrl,
  type ChecklistItem,
  type DutyLine,
  type EuMemberStateCode,
  type Incoterm2020,
  type ShipmentInput,
  type TariffStore,
} from "@/lib/eu";

export const Route = createFileRoute("/eu-tariff")({
  head: () => ({
    meta: [
      { title: "EU duty & GSP+ calculator — Ready4Trade" },
      {
        name: "description",
        content:
          "Compute EU customs duty, GSP+ preference margin, entry price, VAT and landed cost for shipments from Uzbekistan into any of the 27 EU member states.",
      },
    ],
  }),
  component: EuTariffPage,
});

const INCOTERMS: Incoterm2020[] = [
  "EXW",
  "FCA",
  "FOB",
  "CFR",
  "CIF",
  "CPT",
  "CIP",
  "DAP",
  "DPU",
  "DDP",
];

const eur = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(value);

function EuTariffPage() {
  const agg = useAggregates();
  const [store, setStore] = useState<TariffStore>();
  const [storeError, setStoreError] = useState<string>();
  const [query, setQuery] = useState("dried apricots");
  const [cn8, setCn8] = useState("08131000");

  const [input, setInput] = useState<ShipmentInput>({
    cn8: "08131000",
    destination: "DE",
    invoiceValue: 40000,
    invoiceCurrency: "USD",
    exchangeRateToEur: 0.92,
    incoterm: "FOB",
    freightToEuBorder: 3500,
    insuranceToEuBorder: 200,
    freightBorderToDestination: 800,
    otherChargesToBorder: 0,
    netMassKg: 20000,
    originProof: "rex_statement",
    nonManipulationEvidenced: true,
  });
  const [whollyObtained, setWhollyObtained] = useState(true);

  useEffect(() => {
    loadTariffStore().then(setStore, (error: Error) => setStoreError(error.message));
  }, []);

  const results = useMemo(() => (store ? searchLines(store, query, 12) : []), [store, query]);
  const line: DutyLine | undefined = store?.byCn8.get(cn8);

  const patch = (next: Partial<ShipmentInput>) => setInput((current) => ({ ...current, ...next }));

  const breakdown = useMemo(
    () => (line ? computeEuLandedCost(line, { ...input, cn8 }) : undefined),
    [line, input, cn8],
  );
  const competitors = useMemo(
    () =>
      line && breakdown
        ? compareCompetitorRegimes(line, breakdown.customsValueEur, { ...input, cn8 })
        : [],
    [line, breakdown, input, cn8],
  );
  const states = useMemo(
    () =>
      line
        ? compareMemberStates(
            line,
            { ...input, cn8 },
            EU_MEMBER_STATE_LIST.map((state) => state.code),
          )
        : [],
    [line, input, cn8],
  );
  const checklist = useMemo(
    () =>
      line && breakdown
        ? assessRexReadiness({
            originProof: input.originProof,
            nonManipulationEvidenced: input.nonManipulationEvidenced,
            consignmentValueEur: breakdown.customsValueEur,
            line,
            whollyObtained,
            usesImportedMaterials: !whollyObtained,
            listRuleSatisfied: null,
          })
        : [],
    [line, breakdown, input, whollyObtained],
  );

  if (storeError) {
    return (
      <DashboardShell meta={agg.data?.meta}>
        <PageHeading title="EU duty & GSP+ calculator" subtitle={storeError} />
      </DashboardShell>
    );
  }
  if (!store) {
    return (
      <DashboardShell meta={agg.data?.meta}>
        <LoadingPanel />
      </DashboardShell>
    );
  }

  return (
    <DashboardShell meta={agg.data?.meta}>
      <PageHeading
        title="🇺🇿 → 🇪🇺 EU duty, GSP+ and landed cost"
        subtitle="Customs duty is identical in all 27 member states — the EU is a customs union. The member state changes VAT, clearance practice and the corridor entry point, not the duty rate."
        badge="All 27 member states"
      />

      {store.unverifiedCount > 0 && (
        <section className="flex gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-xs leading-relaxed text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <strong>
              {store.unverifiedCount} of {store.lines.length} tariff lines carry unverified seed
              rates.
            </strong>{" "}
            The calculation engine is production-ready; the rates in it are not. Every figure below
            must be confirmed against TARIC before it is quoted to a buyer. Run the ingestion job
            described in <code className="rounded bg-amber-100 px-1">data/eu/README.md</code> to
            replace them.
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)]">
        <div className="space-y-5">
          <div className="surface-panel p-5">
            <h2 className="font-display text-lg">1 · Classify to CN8</h2>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              The EU levies duty at 8 digits and applies measures at 10. An HS6 code is not enough —
              one subheading often splits into lines with different rates, quotas and entry-price
              treatment, so the choice is yours to make, not the tool's to guess.
            </p>
            <div className="relative mt-4">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search a product or type a CN / HS code…"
                className="h-10 w-full rounded-lg border border-transparent bg-secondary pl-9 pr-3 text-sm focus:border-primary/40 focus:bg-card focus:outline-none"
              />
            </div>
            <div className="mt-3 max-h-64 space-y-1 overflow-y-auto">
              {results.map((candidate) => (
                <button
                  key={candidate.cn8}
                  onClick={() => setCn8(candidate.cn8)}
                  className={`w-full rounded-lg border p-2.5 text-left text-xs transition ${
                    candidate.cn8 === cn8
                      ? "border-primary/40 bg-primary/5"
                      : "border-border hover:bg-secondary"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="code-chip text-primary">{candidate.cn8}</span>
                    <span className="text-[10px] text-muted-foreground">
                      HS {candidate.hs6} · {describeDuty(candidate.erga)} MFN
                    </span>
                  </div>
                  <div className="mt-1 leading-snug text-foreground">{candidate.description}</div>
                </button>
              ))}
              {!results.length && (
                <p className="px-1 py-4 text-center text-xs text-muted-foreground">
                  No line matches. The seed store covers Uzbekistan's principal EU-bound exports
                  only; a full CN ingestion adds the remaining ~9,500 lines.
                </p>
              )}
            </div>
          </div>

          <div className="surface-panel p-5">
            <h2 className="font-display text-lg">2 · Consignment</h2>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <Select
                label="Destination member state"
                value={input.destination}
                onChange={(value) => patch({ destination: value as EuMemberStateCode })}
                options={EU_MEMBER_STATE_LIST.map((state) => ({
                  value: state.code,
                  label: state.name,
                }))}
              />
              <Select
                label="Incoterm 2020"
                value={input.incoterm}
                onChange={(value) => patch({ incoterm: value as Incoterm2020 })}
                options={INCOTERMS.map((code) => ({ value: code, label: code }))}
              />
              <Number
                label="Invoice value"
                value={input.invoiceValue}
                onChange={(v) => patch({ invoiceValue: v })}
              />
              <Select
                label="Invoice currency"
                value={input.invoiceCurrency}
                onChange={(value) => patch({ invoiceCurrency: value })}
                options={["USD", "EUR", "UZS"].map((code) => ({ value: code, label: code }))}
              />
              <Number
                label="Customs exchange rate to EUR"
                value={input.exchangeRateToEur}
                step="0.0001"
                onChange={(v) => patch({ exchangeRateToEur: v })}
                hint="Monthly rate under UCC IA Art. 146, not spot"
              />
              <Number
                label="Net mass (kg)"
                value={input.netMassKg}
                onChange={(v) => patch({ netMassKg: v })}
              />
              <Number
                label="Freight to EU border"
                value={input.freightToEuBorder}
                onChange={(v) => patch({ freightToEuBorder: v })}
              />
              <Number
                label="Insurance to EU border"
                value={input.insuranceToEuBorder}
                onChange={(v) => patch({ insuranceToEuBorder: v })}
              />
              <Number
                label="Carriage border → destination"
                value={input.freightBorderToDestination}
                onChange={(v) => patch({ freightBorderToDestination: v })}
                hint="Out of the customs value, into the VAT base"
              />
              {line?.entryPrice && (
                <Number
                  label="Entry price (EUR / 100 kg)"
                  value={input.entryPricePer100Kg ?? 0}
                  onChange={(v) => patch({ entryPricePer100Kg: v })}
                  hint={`Trigger ${line.entryPrice.triggerPrice} EUR / 100 kg`}
                />
              )}
            </div>

            <div className="mt-4 space-y-2 border-t border-border pt-4">
              <Toggle
                label="REX statement on origin held"
                checked={input.originProof === "rex_statement"}
                onChange={(checked) => patch({ originProof: checked ? "rex_statement" : "none" })}
              />
              <Toggle
                label="Non-manipulation evidenced across transit"
                checked={input.nonManipulationEvidenced}
                onChange={(checked) => patch({ nonManipulationEvidenced: checked })}
              />
              <Toggle
                label="Wholly obtained in Uzbekistan"
                checked={whollyObtained}
                onChange={setWhollyObtained}
              />
              <Toggle
                label="Reduced VAT rate claimed"
                checked={Boolean(input.reducedVatClaimed)}
                onChange={(checked) => patch({ reducedVatClaimed: checked })}
              />
            </div>
          </div>
        </div>

        <div className="space-y-5">
          {line && breakdown && (
            <>
              <div className="surface-panel p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-lg">{line.description}</h2>
                    <p className="mt-1 text-xs text-muted-foreground">
                      CN {line.cn8} · third-country duty {describeDuty(line.erga)} ·{" "}
                      {line.gspSensitivity === "sensitive" ? "GSP sensitive" : "GSP non-sensitive"}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={taricConsultationUrl(line.cn8)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-medium"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> TARIC
                    </a>
                    <a
                      href={access2MarketsUrl(line.hs6, input.destination)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg bg-secondary px-3 py-2 text-xs font-medium"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Access2Markets
                    </a>
                  </div>
                </div>

                <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <Metric
                    label="Customs value"
                    value={eur(breakdown.customsValueEur)}
                    note="CIF at EU border"
                  />
                  <Metric
                    label="Duty payable"
                    value={eur(breakdown.regime.dutyEur + breakdown.tradeDefenceEur)}
                    note={REGIME_LABELS[breakdown.regime.regime]}
                    tone={breakdown.regime.dutyEur === 0 ? "good" : "neutral"}
                  />
                  <Metric
                    label="Preference margin"
                    value={eur(breakdown.preferenceMarginEur)}
                    note={`${breakdown.preferenceMarginRate}% of customs value saved vs MFN`}
                    tone={breakdown.preferenceMarginEur > 0 ? "good" : "bad"}
                  />
                  <Metric
                    label={`Import VAT (${breakdown.vatRate}%)`}
                    value={eur(breakdown.vatEur)}
                    note="Recoverable by a registered importer"
                  />
                </div>

                <div className="mt-4 rounded-xl bg-slate-950 p-5 text-white">
                  <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-white/55">
                        Cost that actually erodes your price
                      </div>
                      <div className="mt-1 text-3xl font-bold">
                        {eur(breakdown.irrecoverableCostEur)}
                      </div>
                      <div className="mt-1 text-[11px] text-white/60">
                        Customs value + duty. VAT is excluded because the buyer reclaims it.
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs uppercase tracking-wider text-white/55">
                        Cash at the border
                      </div>
                      <div className="mt-1 text-xl font-semibold">
                        {eur(breakdown.totalAtBorderEur)}
                      </div>
                      <div className="mt-1 text-[11px] text-white/60">
                        Including {eur(breakdown.vatEur)} VAT
                      </div>
                    </div>
                  </div>
                </div>

                <details className="mt-4 rounded-lg bg-secondary/60 p-3 text-xs">
                  <summary className="cursor-pointer font-semibold">
                    How the customs value was derived
                  </summary>
                  <ul className="mt-2 space-y-1 text-muted-foreground">
                    {breakdown.customsValueBasis.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  {breakdown.regime.components.length > 0 && (
                    <ul className="mt-3 space-y-1 border-t border-border pt-2 text-muted-foreground">
                      {breakdown.regime.components.map((component) => (
                        <li key={component.label}>
                          <strong className="text-foreground">{component.label}</strong> —{" "}
                          {eur(component.amountEur)}{" "}
                          <span className="text-[10px]">({component.basis})</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <ul className="mt-3 space-y-1 border-t border-border pt-2 text-muted-foreground">
                    {breakdown.regime.notes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                </details>
              </div>

              <div className="surface-panel p-5">
                <div className="flex items-center gap-2">
                  <Percent className="h-4 w-4 text-primary" />
                  <h2 className="font-display text-base">
                    Against the regimes your competitors ship under
                  </h2>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  The margin that decides a tender is rarely the one against MFN.
                </p>
                <table className="mt-3 w-full text-xs">
                  <thead className="text-muted-foreground">
                    <tr className="text-left">
                      <th className="py-2 font-semibold">Regime</th>
                      <th className="py-2 font-semibold">Applied duty</th>
                      <th className="py-2 text-right font-semibold">Duty</th>
                      <th className="py-2 text-right font-semibold">Gap</th>
                    </tr>
                  </thead>
                  <tbody>
                    {competitors.map((row) => (
                      <tr
                        key={row.regime}
                        className={`border-t border-border ${row.regime === breakdown.regime.regime ? "bg-primary/5 font-semibold" : ""}`}
                      >
                        <td className="py-2">{REGIME_LABELS[row.regime]}</td>
                        <td className="py-2 text-muted-foreground">
                          {describeDuty(row.outcome.applied)}
                        </td>
                        <td className="py-2 text-right">{eur(row.outcome.dutyEur)}</td>
                        <td className="py-2 text-right text-muted-foreground">
                          {row.gapToBestEur > 0 ? `+${eur(row.gapToBestEur)}` : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="surface-panel p-5">
                <div className="flex items-center gap-2">
                  <FileCheck2 className="h-4 w-4 text-primary" />
                  <h2 className="font-display text-base">REX and rules-of-origin readiness</h2>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  The EU withdrew Form A. Preference is claimed by a statement on origin from a
                  registered exporter — without one, the buyer simply pays{" "}
                  {eur(breakdown.mfnComparison.dutyEur)} more.
                </p>
                <div className="mt-3 space-y-2">
                  {checklist.map((item) => (
                    <ChecklistRow key={item.id} item={item} />
                  ))}
                </div>
              </div>

              {breakdown.requirements.length > 0 && (
                <div className="surface-panel p-5">
                  <div className="flex items-center gap-2">
                    <ShieldQuestion className="h-4 w-4 text-primary" />
                    <h2 className="font-display text-base">Clearance requirements beyond duty</h2>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Duty is rarely what stops a consignment. These are.
                  </p>
                  <ul className="mt-3 space-y-2">
                    {breakdown.requirements.map((requirement) => (
                      <li key={requirement.code} className="rounded-lg bg-secondary/60 p-3 text-xs">
                        <div className="font-semibold text-foreground">{requirement.label}</div>
                        <div className="mt-1 text-muted-foreground">{requirement.legalBasis}</div>
                        {requirement.celex && (
                          <a
                            href={`https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=CELEX:${requirement.celex}`}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-primary"
                          >
                            CELEX {requirement.celex} <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {breakdown.warnings.length > 0 && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-center gap-2 text-xs font-semibold text-amber-900">
                    <CircleAlert className="h-4 w-4" /> Check before you quote
                  </div>
                  <ul className="mt-2 space-y-1.5 text-xs leading-relaxed text-amber-900">
                    {breakdown.warnings.map((warning) => (
                      <li key={warning}>• {warning}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="surface-panel p-5">
                <div className="flex items-center gap-2">
                  <Landmark className="h-4 w-4 text-primary" />
                  <h2 className="font-display text-base">
                    The same consignment into all 27 member states
                  </h2>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">
                  Duty is constant across the customs union. The entire spread below is VAT — a
                  buyer cash-flow question, not a competitiveness one, and it disappears where
                  postponed accounting is available.
                </p>
                <div className="mt-3 max-h-80 overflow-y-auto rounded-lg border border-border">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-secondary text-muted-foreground">
                      <tr className="text-left">
                        <th className="px-3 py-2 font-semibold">Member state</th>
                        <th className="px-3 py-2 text-right font-semibold">Duty</th>
                        <th className="px-3 py-2 text-right font-semibold">VAT</th>
                        <th className="px-3 py-2 text-right font-semibold">Cash at border</th>
                        <th className="px-3 py-2 font-semibold">Postponed VAT</th>
                      </tr>
                    </thead>
                    <tbody>
                      {states.map((row) => (
                        <tr
                          key={row.destination}
                          className={`border-t border-border ${row.destination === input.destination ? "bg-primary/5 font-semibold" : ""}`}
                        >
                          <td className="px-3 py-2">
                            {
                              EU_MEMBER_STATE_LIST.find((state) => state.code === row.destination)
                                ?.name
                            }
                          </td>
                          <td className="px-3 py-2 text-right">{eur(row.result.regime.dutyEur)}</td>
                          <td className="px-3 py-2 text-right">
                            {row.result.vatRate}% · {eur(row.result.vatEur)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {eur(row.result.totalAtBorderEur)}
                          </td>
                          <td className="px-3 py-2">
                            {row.result.vatRecoverable &&
                            EU_MEMBER_STATE_LIST.find((s) => s.code === row.destination)?.vat
                              .postponedAccounting ? (
                              <span className="inline-flex items-center gap-1 text-emerald-700">
                                <BadgeCheck className="h-3 w-3" /> yes
                              </span>
                            ) : (
                              <span className="text-muted-foreground">no</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="surface-panel p-5">
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-primary" />
                  <h2 className="font-display text-base">Provenance</h2>
                </div>
                <ul className="mt-2 space-y-2 text-xs text-muted-foreground">
                  {breakdown.provenance.map((item, index) => (
                    <li
                      key={`${item.source}-${index}`}
                      className="flex flex-wrap items-center gap-2"
                    >
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                          item.status === "official"
                            ? "bg-emerald-50 text-emerald-700"
                            : item.status === "verified_manual"
                              ? "bg-blue-50 text-blue-700"
                              : "bg-amber-50 text-amber-800"
                        }`}
                      >
                        {item.status.replace(/_/g, " ")}
                      </span>
                      <span>{item.source}</span>
                      <span className="text-[10px]">
                        {item.asOf ? `as of ${item.asOf}` : "no retrieval date"}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-border bg-secondary/50 p-4 text-xs leading-relaxed text-muted-foreground">
        This is a screening estimate, not a customs ruling. Classification, valuation, preference
        eligibility and applicable measures are determined by the customs authority of the member
        state of import on the facts of the specific consignment. For a binding answer, apply for
        Binding Tariff Information. Anti-dumping, countervailing, safeguard, quota, excise and
        agricultural components are only modelled where present in the tariff store.
      </section>
    </DashboardShell>
  );
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  const tone =
    item.state === "ok"
      ? "border-emerald-200 bg-emerald-50"
      : item.state === "blocking"
        ? "border-red-200 bg-red-50"
        : item.state === "attention"
          ? "border-amber-200 bg-amber-50"
          : "border-border bg-secondary/50";
  const Icon =
    item.state === "ok" ? CircleCheck : item.state === "blocking" ? CircleAlert : CircleHelp;
  return (
    <div className={`rounded-lg border p-3 text-xs ${tone}`}>
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <div>
          <div className="font-semibold text-foreground">{item.title}</div>
          <p className="mt-1 leading-relaxed text-muted-foreground">{item.detail}</p>
          <div className="mt-1 flex flex-wrap gap-3">
            {item.legalBasis && (
              <span className="text-[10px] text-muted-foreground">{item.legalBasis}</span>
            )}
            {item.actionUrl && (
              <a
                href={item.actionUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary"
              >
                Official guidance <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({
  label,
  value,
  note,
  tone = "neutral",
}: {
  label: string;
  value: string;
  note: string;
  tone?: "neutral" | "good" | "bad";
}) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div
        className={`mt-1 text-lg font-bold ${tone === "good" ? "text-emerald-700" : tone === "bad" ? "text-amber-700" : ""}`}
      >
        {value}
      </div>
      <div className="mt-1 text-[10px] leading-snug text-muted-foreground">{note}</div>
    </div>
  );
}

function Number({
  label,
  value,
  onChange,
  hint,
  step = "any",
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  hint?: string;
  step?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold">{label}</span>
      <input
        type="number"
        min="0"
        step={step}
        value={value}
        onChange={(event) => onChange(globalThis.Number(event.target.value) || 0)}
        className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary/50"
      />
      {hint && <span className="mt-1 block text-[10px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold">{label}</span>
      <select
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function Toggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2.5 text-xs">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 accent-[color:var(--color-primary)]"
      />
      <span>{label}</span>
    </label>
  );
}
